/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/

import { inject, Injectable } from "@angular/core";
import { forkJoin, Observable, of } from "rxjs";
import { first, map } from "rxjs/operators";
import { AbstractObjectList, uuid } from "@src/app/model/abstract-object";
import { User } from "@src/app/model/user";
import { CurrentUserService } from "@src/app/modules/user/current-user.service";
import { BatchType, ImportParser } from '@src/app/modules/importer/import-page/Import-parser-interface';
import { ImportDoc } from "@src/app/modules/importer/ImportDoc";
import { GeneralParser } from "@src/app/modules/importer/General/GeneralParserService";
import { ParseOptionBase, ParseValidator } from "@src/app/modules/importer/General/ParseOptionBase";
import { ParseOptionBaseMapped } from "@src/app/modules/importer/General/ParseOptionBaseMapped";
import { POBValidators, requiredNumber, validateDatePOB } from "@src/app/modules/importer/General/POBValidators";
import { UnitService } from "@src/app/modules/unit/unit.service";
import { ImportRow } from "@src/app/modules/importer/ImportRow";
import { ImportField } from "@src/app/modules/importer/ImportField";
import { BCodeService } from "@src/app/modules/budget/bcode.service";
import { ScheduleService } from "@src/app/modules/budget/schedule.service";
import { ArTxnService } from "@src/app/modules/txn/ar-txn.service";
import { PeriodService } from "@src/app/modules/budget/period.service";
import { Period } from "@src/app/model/period";
import { TenantService } from '@src/app/shared/tenant.service';
import { MyInjector } from "@src/app/app.module";
import { HttpClient } from "@angular/common/http";
import { ParseOptionBaseBCode } from "@src/app/modules/importer/General/ParseOptionBaseBCode";
import { BCode } from "@src/app/model/bcode";
import { BankAccountService } from "@src/app/modules/txn/bank-account.service";
import { BankAccount } from "@src/app/model/bankAccount";
import { PersonService } from "@src/app/pages/person-page/person.service";
import { Person } from "@src/app/model/person";

interface ItemInput {
    unitId: string;
    typeId: string;
    txnDate: string;
    totalAmount: number;
    reference: string;
    lineAmount: number;
    bCodeId: string;
    notes: string;
    scheduleId: string;
    ownerIntegrationId: string;
}

interface LineItem {
    bCodeId: string;
    scheduleId: string;
    amount: number;
    notes: string;
}

interface ChargeOutput {
    unitId: string;
    typeId: string;
    txnDate: string;
    notes: string;
    amount: number;
    ownerId?: uuid;
    lineItems: LineItem[];
}

interface CreditOutput {
    unitId: string;
    typeId: string;
    txnDate: string;
    notes: string;
    amount: number;
    ownerId?: uuid;
    lineItems: LineItem[];
}

interface ReceiptOutput {
    unitId: string;
    typeId: string;
    txnDate: string;
    notes: string;
    amount: number;
    ownerId?: uuid;
    bankId: uuid;
}

interface ArImports {
    charges: (CreditOutput|ChargeOutput)[],
    receipts: ReceiptOutput[];
}

@Injectable({
    providedIn: 'root'
})
export class ArParserService implements ImportParser {

    gps = new GeneralParser();
    unitSvc = inject(UnitService);
    personSvc = inject(PersonService);
    people: Person[] = [];
    currentUserSvc = inject(CurrentUserService);
    bCodeSvc = inject(BCodeService);
    scheduleSvc = inject(ScheduleService);
    arSvc = inject(ArTxnService);
    periodSvc = inject(PeriodService);
    bankAccountSvc = inject(BankAccountService);

    batchType: BatchType = 'ArImport';
    bankAccounts: BankAccount[] = [];

    periods : Period[] = [];

    importDoc: ImportDoc;
    currentUser: User;
    importCharges = new Map<string, ChargeOutput>();
    importCredits = new Map<string, CreditOutput>();
    importReceipts: ReceiptOutput[] = [];

    unitNamePOB = new ParseOptionBaseMapped('Unit', 'unitId', [POBValidators.requiredPOB()]).makeSilent();
    ownerIntegrationIdPOB = new ParseOptionBaseMapped('Owner Integration Id', 'ownerIntegrationId', []).makeSilent().allowNull();
    txnDatePOB = new ParseOptionBase('Transaction Date', 'txnDate')
        .addValidator(validateDatePOB())
        .addValidator({
        validateRow: (field: ImportField) => {
            const val = field.getValue();
            const prd = this.periods.find( p => p.to >= val && p.from <= val);
            if (!prd) {
                return $localize` There is no period for date ${val}`
            } if (prd.statusId !== Period.STATUS.OPEN.id) {
                return $localize` Period ${prd.name} is not open for transactions`;
            }
            return null;
        }
    });
    notesPOB = new ParseOptionBase('Note', 'notes');

    typeValidator = {
        validateRow: (field: ImportField) => {
            const val = field.getValue();
            if (val === 'Charge' || val === 'Credit' || val === 'Receipt') {
                return null;
            }
            return $localize `Invalid type ${val} - Expected Charge, Credit or Receipt`;
        }
    } as ParseValidator
    typePOB = new ParseOptionBase('Type', 'typeId').addValidator(this.typeValidator);
    bCodePOB = new ParseOptionBaseBCode('Budget Code', 'bCodeId', [POBValidators.requiredPOB()]);
    schedulePOB = new ParseOptionBaseMapped('Schedule', 'scheduleId');
    amountPOB = new ParseOptionBase('Amount', 'lineAmount').addValidator(requiredNumber);
    invoiceRefPOB = new ParseOptionBase('Reference', 'reference');

    availableImportItems = [
        this.unitNamePOB,
        this.typePOB,
        this.txnDatePOB,
        this.notesPOB,
        this.bCodePOB,
        this.schedulePOB,
        this.amountPOB,
        this.invoiceRefPOB,
        this.ownerIntegrationIdPOB
    ];

    setUp() {
        this.importDoc = new ImportDoc();

        return forkJoin({
            currentUser: this.currentUserSvc.getCurrentUser().pipe(first()),
            units: this.unitSvc.get(true),
            bCodes: this.bCodeSvc.get<BCode>(true),
            schedules: this.scheduleSvc.get(true),
            periods: this.periodSvc.get(true),
            banks: this.bankAccountSvc.get<BankAccount>(true),
            people: this.personSvc.get<Person>(true),
        }).pipe(map(result => {
            this.currentUser = result.currentUser;
            this.periods = result.periods as Period[];
            this.unitNamePOB.setOptions(result.units, this.importDoc);
            this.bCodePOB.setOptions(result.bCodes, this.importDoc);
            this.schedulePOB.setOptions(result.schedules, this.importDoc);
            this.bankAccounts = result.banks;
            const integrationIds: {id: uuid, name: string}[] = [];
            result.people.forEach ( p=> {
                if (p.integrationId) {
                    integrationIds.push( {id: p.id, name: p.integrationId})
                }
            });
            this.ownerIntegrationIdPOB.setOptions(integrationIds, this.importDoc);
            return true;
        }));
    }

    parseRows(rows: unknown[][]): ImportDoc {
        this.gps.availableImportItems = this.availableImportItems;
        this.gps.parseRows(rows, this.importDoc);
        return this.importDoc;
    }

    getCreditKey(c: ItemInput) {
        return `${c.unitId}-${c.txnDate}-${c.reference}`;
    }

    getCredit(c: ItemInput): ChargeOutput {
        const key = this.getCreditKey(c);
        if (this.importCredits.has(key)) {
            return this.importCredits.get(key)
        }
        const co = {
            unitId: c.unitId,
            typeId: c.typeId,
            txnDate: c.txnDate,
            notes: c.reference ? `Import ${c.reference}` : 'Imported',
            amount: 0,
            lineItems: []
        } as CreditOutput;
        if (c.ownerIntegrationId) {
            co.ownerId = c.ownerIntegrationId;
        }        
        this.importCredits.set(key, co);
        return co;
    }

    processCredit(c: ItemInput, line: ImportRow) {
        if (c.lineAmount > 0) {
            line.addError('Credit should be an amount less than zero');
        }
        const co = this.getCredit(c);
        co.lineItems.push({
            bCodeId: c.bCodeId,
            scheduleId: c.scheduleId,
            amount: c.lineAmount,
            notes: c.notes
        })
        co.amount += c.lineAmount;
    }

    processReceipt(c: ItemInput, line: ImportRow) {
        let hasAcct = false;
        let bank: BankAccount;
        if (c.bCodeId) {
            hasAcct = true;
            bank = this.bankAccounts.find(ba => ba.bCodeId === c.bCodeId);
        }

        if (hasAcct && !bank) {
            line.addError(`Account is not a bank account not found`);
        } else {
            const txn = {
                unitId: c.unitId,
                typeId: c.typeId,
                txnDate: c.txnDate,
                notes: c.notes,
                amount: c.lineAmount,
            } as ReceiptOutput;
            if (bank) {
                txn.bankId = bank.id;
            }
            if (c.ownerIntegrationId) {
                txn.ownerId = c.ownerIntegrationId;
            }            
            this.importReceipts.push(txn);
        }
    }

    getChargeKey(c: ItemInput) {
        return `${c.unitId}-${c.txnDate}-${c.reference}`;
    }

    getCharge(c: ItemInput) : ChargeOutput {
        const key = this.getChargeKey(c);
        if (this.importCharges.has(key)) {
            return this.importCharges.get(key)
        }
        const co = {
            unitId: c.unitId,
            typeId: c.typeId,
            txnDate: c.txnDate,
            notes: c.reference ? `Import ${c.reference}` : 'Imported',
            amount: 0,
            lineItems: []
        } as ChargeOutput;
        if (c.ownerIntegrationId) {
            co.ownerId = c.ownerIntegrationId;
        }
        this.importCharges.set(key, co);
        return co;
    }

    processCharge(c: ItemInput, line: ImportRow) {
        if (c.lineAmount < 0) {
            line.addError('Charge should be an amount greater than zero');
        }
        const co = this.getCharge(c);
        co.lineItems.push({
            bCodeId: c.bCodeId,
            scheduleId: c.scheduleId,
            amount: c.lineAmount,
            notes: c.notes
        })
        co.amount += c.lineAmount;
    }

    validateData(postItems: ItemInput[]) {
        const dataRows = this.importDoc.getGeneralRows();
        console.log({dataRows, postItems});
        let receiptCnt = 0;
        let negativeReceipts = 0;

        for (let i = 0; i < postItems.length; i++) {
            const line = dataRows[i];
            const data = postItems[i];
            if (!data.txnDate) {
                line.addError('Transaction date is required - Ensure you have a valid date value in a column mapped to Transaction Date');
            }
            if (!data.unitId) {
                line.addError('Unit is required - Ensure you have a valid unit name in a column mapped to Unit');
            }
            if (data.typeId === 'Charge') {
                this.processCharge(data, line);
            } else if (data.typeId === 'Credit') {
                if (data['amount'] > 0) {
                    line.addError('Credits should be an amount less than zero');
                }
                this.processCredit(data, line);
            } else if (data.typeId === 'Receipt') {
                receiptCnt += 1;
                if (data['amount'] > 0) {
                    negativeReceipts += 1;
                }
                this.processReceipt(data, line);
            } else {
                line.addError('Do not know what type of transaction this is');
            }
            if (!data.scheduleId) {
                line.addError('Needs a schedule to be assigned');
            }
        }

        if (negativeReceipts > (receiptCnt * .8)) {
            const msg = `'More than 80% of receipts are positive.
            Normal receipts should be entered as negative figures, to reduce the unit balance outstanding.
            There are some exceptional cases, where you may need to record a positive receipt,
            that increases the unit balance such as a refund or an imported allocation, but this is exceptional.
            We assume if more than 80% are negative, you are accidentally importing your receipts as positive amounts. `;
            this.importDoc.addNote(msg, null, true);
        }
        this.importDoc.finalise();
    }

    postToServer(): Observable<AbstractObjectList> {
        //const postItems = this.gps.getItemsToPost((row: ImportRow) => {
            //const u = {id: null, revision: null};
            /*
            const exists = this.units.find( u => u.name === row.getValue(this.unitNamePOB.fieldName));
            if (exists) {
                u.id = exists.id;
                u.revision = exists.revision;
            }
            */
            //return u;
        //});
        const postItems = this.gps.getItemsToPost() as unknown as ItemInput[];
        console.log(postItems);
        this.validateData(postItems);

        if (this.importDoc.hasErrors()) {
            return of(null);
        }

        const postData : ArImports = {
            charges: [],
            receipts: [],
        }
        const inputs: (ChargeOutput|CreditOutput)[] = [];
        for( const v of this.importCharges.values()) inputs.push(v);
        for (const v of this.importCredits.values()) inputs.push(v);
        postData.charges = inputs.sort((a,b) => a.txnDate.localeCompare(b.txnDate));
        postData.receipts = this.importReceipts.sort((a, b) => a.txnDate.localeCompare(b.txnDate));
        console.log(postData);

        const url = TenantService.ajaxPath + 'importFile/' + this.batchType;
        const http = MyInjector.instance.get(HttpClient);
        return http.post<AbstractObjectList>(url, postData);
    }
}
